import {
  Expand01Icon,
  InfoCircleIcon,
  Minimize01Icon,
  Nest01Icon,
} from '@langchain/untitled-ui-icons';
import { LinearProgress } from '@mui/joy';

import * as React from 'react';
import { createContext, useContext, useRef, useState } from 'react';
import ReactFlow, {
  EdgeProps,
  Handle,
  NodeProps,
  Position,
  Edge as ReactFlowEdge,
  ReactFlowInstance,
  Node as ReactFlowNode,
  getBezierPath,
  useStore,
} from 'reactflow';
import 'reactflow/dist/style.css';

import css from '@/Pages/Graph/src/components/debug/graph/GraphChartView.module.css';
import { overrideReservedLabelsColor } from '@/Pages/Graph/src/components/debug/graph/graph.utils';
import { Graph } from '@/Pages/Graph/src/data/misc';
import {
  hashString,
  hslaToStr,
  hslaWithAlpha,
  hslaWithLightness,
  hslaWithMinLightness,
  numberToColor,
} from '@/Pages/Graph/src/utils';
import EmptyState from '@/components/EmptyState';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { ExpandableErrorAlert } from '@/components/ExpandableErrorAlert';
import { useColorScheme } from '@/hooks/useColorScheme';
import {
  getBidirectionPath,
  getEdgeParams,
  getSelfLinkedPath,
  useInitializeGraph,
  useResizeGraph,
} from '@/utils/graph-render-utils';
import { cn } from '@/utils/tailwind';

// ----- Node Component -----

const GraphNodeView = (props: NodeProps) => {
  const { isDarkMode } = useColorScheme();
  const hsla = overrideReservedLabelsColor(
    { label: props.data.label, isDarkMode },
    numberToColor(hashString(props.data.label))
  );

  const colorHsla = overrideReservedLabelsColor(
    { label: props.data.label, isDarkMode },
    hslaWithLightness(hsla, isDarkMode ? 80 : 40)
  );

  const notStartOrEndNode = props.id !== '__start__' && props.id !== '__end__';

  const { toggleSubgraphSelection } = useContext(SubgraphSelectionContext);

  return (
    <div className="relative">
      <div key={props.id} className="w-full">
        <Handle
          type="target"
          position={Position.Top}
          id={`${props.id}-target`}
          className="!opacity-0"
        />
        <div className="flex flex-col place-items-center">
          <div className="w-full text-center">
            <div
              className={cn(
                'group relative rounded-md border p-2 font-medium',
                !notStartOrEndNode && 'rounded-full'
              )}
              style={{
                color: hslaToStr(colorHsla),
                backgroundColor: hslaToStr(
                  hslaWithAlpha(hsla, props.selected ? 0.2 : 0.1)
                ),
                borderColor: hslaToStr(hslaWithMinLightness(hsla, 60)),
              }}
              onClick={() => toggleSubgraphSelection(props.data.label)}
            >
              <div className="z-[2] flex items-center justify-center gap-1.5">
                {props.data.isSubgraph && (
                  <div className="pointer-events-auto relative size-4">
                    <Expand01Icon className="absolute inset-0 size-4 opacity-0 transition-opacity group-hover:opacity-100" />
                    <Nest01Icon className="absolute inset-0 size-4 opacity-100 transition-opacity group-hover:opacity-0" />
                  </div>
                )}
                <span className="leading-none">{props.data.label}</span>
              </div>
            </div>
          </div>
        </div>
        <Handle
          type="source"
          position={Position.Bottom}
          id={`${props.id}-source`}
          className="!opacity-0"
        />
      </div>
    </div>
  );
};

// ----- Subgraph Node Component -----

const SubgraphNodeView = (props: NodeProps) => {
  const { isDarkMode } = useColorScheme();
  const hsla = overrideReservedLabelsColor(
    props.data.label,
    numberToColor(hashString(props.data.label))
  );

  const colorHsla = hslaWithLightness(hsla, isDarkMode ? 80 : 40);
  const { toggleSubgraphSelection } = useContext(SubgraphSelectionContext);
  return (
    <div
      className={cn(
        'group absolute inset-0 -top-6 flex cursor-pointer flex-col place-items-center rounded-lg border border-secondary text-ls-black transition-opacity',
        'opacity-100'
      )}
      style={{
        color: hslaToStr(colorHsla),
        backgroundColor: hslaToStr(
          hslaWithAlpha(hsla, props.selected ? 0.2 : 0.1)
        ),
        borderColor: hslaToStr(hslaWithMinLightness(hsla, 60)),
      }}
      onClick={() => toggleSubgraphSelection(props.data.label)}
    >
      <div className="mt-2 flex items-center justify-center gap-1.5">
        <div className="pointer-events-auto relative size-4">
          <Minimize01Icon className="absolute inset-0 size-4 opacity-0 transition-opacity group-hover:opacity-100" />
          <Nest01Icon className="absolute inset-0 size-4 opacity-100 transition-opacity group-hover:opacity-0" />
        </div>
        <div className="text-xs font-medium">{props.data.label}</div>
      </div>

      <span
        className={cn(
          css.stripes,
          'pointer-events-none absolute inset-x-0 z-[1] overflow-hidden opacity-0 transition-opacity',
          'top-0 h-1/2 rounded-t-md'
        )}
        style={{ color: hslaToStr(hslaWithAlpha(colorHsla, 0.15)) }}
      />
    </div>
  );
};

// ----- Edge Component -----

const GraphEdgeView: React.FC<EdgeProps> = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  source,
  target,
  style = {},
  data,
}) => {
  const { isDarkMode } = useColorScheme();
  const sourceNode = useStore(
    React.useCallback((store) => store.nodeInternals.get(source), [source])
  );
  const targetNode = useStore(
    React.useCallback((store) => store.nodeInternals.get(target), [target])
  );
  // Check if this is a bidirectional edge
  const isBidirectional = useStore(
    (s) => !!s.edges.find((e) => e.source === target && e.target === source)
  );

  const isConditionalBidirectional = useStore((s) => {
    const currentEdge = s.edges.find(
      (e) => e.source === source && e.target === target
    );
    const bidirectionalEdge = s.edges.find(
      (e) => e.source === target && e.target === source
    );

    return (
      !!bidirectionalEdge &&
      !!(bidirectionalEdge.data.conditional || currentEdge?.data.conditional)
    );
  });
  const isSelfLinked = source === target;

  if (!sourceNode || !targetNode) return null;
  const { sx, sy, tx, ty, sourcePos, targetPos } = getEdgeParams(
    sourceNode,
    targetNode
  );
  const [edgePath] = isSelfLinked
    ? getSelfLinkedPath(sourceNode)
    : (isConditionalBidirectional ? getBidirectionPath : getBezierPath)({
        sourceX: sx,
        sourceY: sy,
        sourcePosition: sourcePos,
        targetPosition: targetPos,
        targetX: tx,
        targetY: ty,
      });

  const sourceColor = overrideReservedLabelsColor(
    { label: sourceNode.data.label, isDarkMode },
    hslaWithMinLightness(numberToColor(hashString(sourceNode.data.label)), 60)
  );

  const sourceEdgeColor = hslaWithAlpha(sourceColor, 0.8);

  return (
    <>
      <path
        id={id}
        className="react-flow__edge-path"
        d={edgePath}
        style={{
          ...style,
          stroke:
            isBidirectional && !isConditionalBidirectional && !isSelfLinked
              ? `url(#gradient-${id})`
              : hslaToStr(sourceEdgeColor),
          strokeWidth: 1.5,
          strokeDasharray: data?.conditional ? '3 1' : undefined,
        }}
        markerEnd={`url(#arrow-${id})`}
      />
      <defs>
        {isBidirectional && !isConditionalBidirectional && !isSelfLinked ? (
          <linearGradient
            id={`gradient-${id}`}
            x1={sourceX}
            y1={sourceY}
            x2={targetX}
            y2={targetY}
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#b1b1b7" offset="0%" />
            <stop stopColor="#d1d1d7" offset="50%" />
            <stop stopColor="#b1b1b7" offset="100%" />
          </linearGradient>
        ) : null}
        <marker
          id={`arrow-${id}`}
          markerWidth="30"
          markerHeight="30"
          viewBox="-10 -10 20 20"
          markerUnits="strokeWidth"
          orient="auto-start-reverse"
          refX="0"
          refY="0"
        >
          <polyline
            strokeLinecap="round"
            strokeLinejoin="round"
            points="-5,-4 0,0 -5,4 -5,-4"
            style={{ fill: hslaToStr(sourceColor) }}
          />
        </marker>
      </defs>
    </>
  );
};

// ----- Main Component -----

const nodeTypes = { node: GraphNodeView, subgraph: SubgraphNodeView };
const edgeTypes = { edge: GraphEdgeView };

export const GraphPreview = ({
  graphData,
  isLoading,
  error,
  className,
}: {
  graphData: Graph | undefined;
  isLoading: boolean;
  error: unknown | undefined;
  className?: string | undefined;
}) => {
  const [nodes, setNodes] = useState<ReactFlowNode[]>([]);
  const [edges, setEdges] = useState<ReactFlowEdge[]>([]);
  const [visible, setVisible] = useState(false);
  const [subgraphSelection, setSubgraphSelection] = useState<Set<string>>(
    new Set()
  );
  const toggleSubgraphSelection = (subgraph: string) => {
    setSubgraphSelection((prev) => {
      const newSet = new Set(prev);
      newSet.has(subgraph) ? newSet.delete(subgraph) : newSet.add(subgraph);
      return newSet;
    });
  };
  // Process graph data to create nodes and edges

  useInitializeGraph({
    graphData,
    subgraphSelection,
    setNodes,
    setEdges,
  });

  const reRenderKey = Array.from(subgraphSelection).join(',');
  const reRenderKeyRef = React.useRef(reRenderKey);

  React.useLayoutEffect(() => {
    if (reRenderKeyRef.current === reRenderKey) return;
    reRenderKeyRef.current = reRenderKey;
    setVisible(false);
  }, [reRenderKey]);

  const instanceRef = useRef<ReactFlowInstance | null>(null);

  const ref = React.useRef<HTMLDivElement>(null);
  useResizeGraph(ref);

  // Handle empty states and loading
  if (isLoading) {
    return (
      <div>
        <LinearProgress />
      </div>
    );
  }

  if (error) {
    return (
      <div
        className={cn(
          'flex flex-1 flex-col items-center justify-center gap-3',
          className
        )}
      >
        <EmptyState
          icon={<InfoCircleIcon />}
          title="Failed to preview graph"
          description="Unable to load graph preview."
        />
      </div>
    );
  }

  if (!graphData) {
    return (
      <div
        className={cn(
          'flex flex-1 flex-col items-center justify-center gap-3',
          className
        )}
      >
        <EmptyState
          title="No graph data"
          description="Select a graph to preview."
        />
      </div>
    );
  }

  return (
    <SubgraphSelectionContext.Provider
      value={{ subgraphSelection, toggleSubgraphSelection }}
    >
      <ErrorBoundary
        fallback={(error) => (
          <div className="flex flex-1 flex-col items-stretch justify-center gap-4 px-8 text-left">
            <span className="text-lg font-medium tracking-tighter">
              Failed to render graph
            </span>
            <ExpandableErrorAlert error={error} />
          </div>
        )}
      >
        <div
          className={cn(
            className,
            'flex flex-1 flex-col text-sm opacity-0',
            visible && 'opacity-100 transition-opacity'
          )}
          ref={ref}
        >
          <ReactFlow
            key={reRenderKey}
            className="flex-1"
            nodes={nodes}
            edges={edges}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            onInit={(inst) => {
              instanceRef.current = inst;
              setTimeout(() => {
                inst.fitView({ padding: 0.2 });
                setVisible(true);
              }, 100);
            }}
            minZoom={0.1}
            maxZoom={1.5}
            proOptions={{ hideAttribution: true }}
            fitView
            // Disable pan and zoom
            panOnDrag={false}
            zoomOnScroll={false}
            zoomOnPinch={false}
            zoomOnDoubleClick={false}
            zoomActivationKeyCode={null}
            panActivationKeyCode={null}
          />
        </div>
      </ErrorBoundary>
    </SubgraphSelectionContext.Provider>
  );
};

const SubgraphSelectionContext = createContext<{
  subgraphSelection: Set<string>;
  toggleSubgraphSelection: (subgraph: string) => void;
}>({
  subgraphSelection: new Set(),
  toggleSubgraphSelection: () => {},
});
